import type { UnitType } from "@/features/quotation/types";
import { AlertTriangle } from "lucide-react";
import { useId } from "react";
import {
  getQuotationPriceInputLabel,
  isDirectKgUnit,
  usesKgPerBox,
  usesUnitsPerBox,
} from "@/features/quotation/unitLabels";
import { formatBRL, formatMoneyInput } from "@/lib/timeAgo";
import { inputStyle, labelStyle } from "./quotationDetailShared";

type PricePreview = {
  label: string;
  value: number;
};

type QuotationPriceSectionProps = {
  unitType: UnitType;
  pricePerKg: string;
  pricePerBox: string;
  onPricePerKgChange: (value: string) => void;
  onPricePerBoxChange: (value: string) => void;
};

export function QuotationPriceSection({
  unitType,
  pricePerKg,
  pricePerBox,
  onPricePerKgChange,
  onPricePerBoxChange,
}: QuotationPriceSectionProps) {
  const inputId = useId();
  return (
    <div className="quotation-editor-field">
      <label htmlFor={inputId} style={labelStyle}>
        Preço
      </label>
      <input
        id={inputId}
        aria-label={getQuotationPriceInputLabel(unitType)}
        style={inputStyle}
        type="text"
        inputMode="numeric"
        value={isDirectKgUnit(unitType) ? pricePerKg : pricePerBox}
        onChange={(event) =>
          isDirectKgUnit(unitType)
            ? onPricePerKgChange(formatMoneyInput(event.target.value))
            : onPricePerBoxChange(formatMoneyInput(event.target.value))
        }
        placeholder="0,00"
      />
    </div>
  );
}

type QuotationUnitSectionProps = {
  unitType: UnitType;
  kgPerBox: string;
  unitsPerBox: string;
  onUnitTypeChange: (unitType: UnitType) => void;
  onKgPerBoxChange: (value: string) => void;
  onUnitsPerBoxChange: (value: string) => void;
};

export function QuotationUnitSection({
  unitType,
  kgPerBox,
  unitsPerBox,
  onUnitTypeChange,
  onKgPerBoxChange,
  onUnitsPerBoxChange,
}: QuotationUnitSectionProps) {
  return (
    <>
      <div className="quotation-editor-field">
        <label htmlFor="quotation-editor-unit" style={labelStyle}>
          Unidade
        </label>
        <select
          id="quotation-editor-unit"
          value={unitType}
          onChange={(event) => onUnitTypeChange(event.target.value as UnitType)}
        >
          {(["cx_kg", "cx_un", "kg", "sc_kg", "bdj", "un"] as const).map(
            (option) => (
              <option key={option} value={option}>
                {
                  (
                    {
                      cx_kg: "Caixa por kg",
                      cx_un: "Caixa por unidade",
                      kg: "Quilograma",
                      sc_kg: "Saco por kg",
                      bdj: "Bandeja",
                      un: "Unidade",
                    } satisfies Record<UnitType, string>
                  )[option]
                }
              </option>
            ),
          )}
        </select>
      </div>

      {usesKgPerBox(unitType) ? (
        <div className="quotation-editor-field quotation-editor-extra">
          <label htmlFor="quotation-editor-kg" style={labelStyle}>
            {unitType === "sc_kg" ? "Kg por saco" : "Kg por caixa"}
          </label>
          <input
            id="quotation-editor-kg"
            style={inputStyle}
            type="number"
            step="0.1"
            min="0"
            value={kgPerBox}
            onChange={(event) => onKgPerBoxChange(event.target.value)}
            placeholder="Ex: 20"
          />
        </div>
      ) : null}

      {usesUnitsPerBox(unitType) ? (
        <div className="quotation-editor-field quotation-editor-extra">
          <label htmlFor="quotation-editor-units" style={labelStyle}>
            Unidades por caixa
          </label>
          <input
            id="quotation-editor-units"
            style={inputStyle}
            type="number"
            step="1"
            min="0"
            value={unitsPerBox}
            onChange={(event) => onUnitsPerBoxChange(event.target.value)}
            placeholder="Ex: 100"
          />
        </div>
      ) : null}
    </>
  );
}

type QuotationPricePreviewProps = {
  pricePreview: PricePreview | null;
};

export function QuotationPricePreview({
  pricePreview,
}: QuotationPricePreviewProps) {
  if (!pricePreview) return null;

  return (
    <div
      className="quotation-editor-preview"
      style={{
        padding: "0.625rem 0.75rem",
        background: "var(--login-bg)",
        border: "1px solid var(--login-border)",
        borderRadius: "0.5rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: "0.8125rem",
          color: "var(--login-muted)",
          letterSpacing: "0.04em",
        }}
      >
        Preço por {pricePreview.label}
      </span>
      <span
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontWeight: 700,
          fontSize: "1.125rem",
          color: "var(--login-accent)",
        }}
      >
        {formatBRL(pricePreview.value)}
      </span>
    </div>
  );
}

export function QuotationSupplierChangeWarning() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.375rem",
        padding: "0.375rem 0.5rem",
        background: "#d9770612",
        borderRadius: "0.25rem",
      }}
    >
      <AlertTriangle size={12} color="#d97706" style={{ flexShrink: 0 }} />
      <span
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.75rem",
          color: "#d97706",
        }}
      >
        Alterar o fornecedor atualizará todos os pedidos com este item.
      </span>
    </div>
  );
}
